/**
 * Glossary loader + lookup.
 *
 * The glossary JSON is generated by `tools/build_glossary.py` from the
 * per-game catalogue docs (`docs/{defender,stargate,robotron}_sound_catalogue.md`)
 * and shipped as a static asset at `/data/glossary.json`.
 *
 * Each entry pairs a command code (hex, no `$`) with a routine name + engine
 * + 1-2 sentence blurb.  The UI uses this for live tooltips as the user
 * types a hex command — "what does $11 actually do?"
 */
import type { GameKind } from "../board/soundboard.ts";

export interface GlossaryEntry {
  /** Human-readable name (e.g. "Lightning", "Heartbeat distorto"). */
  name: string;
  /** Internal routine label from the ROM source (e.g. "LITE", "HBDV"). */
  routine: string;
  /** Engine family (e.g. "LFSR", "GWAVE", "VARI"); empty for silence. */
  engine: string;
  /** Optional one-line description from the catalogue's Notes column. */
  blurb?: string;
}

/** Explanation of an engine name or technique term (LFSR, GWAVE, …). */
export interface TermEntry {
  /** Title shown at the top of the popover (e.g. "LFSR — linear-feedback shift register"). */
  title: string;
  /** 1-sentence "what is this?" line. */
  what: string;
  /** 1-sentence "how does it work mechanically?" line. */
  how: string;
  /** 1-sentence "where is it used in these ROMs?" line. */
  where: string;
}

export interface Glossary {
  defender: Record<string, GlossaryEntry>;
  stargate: Record<string, GlossaryEntry>;
  robotron: Record<string, GlossaryEntry>;
  terms: Record<string, TermEntry>;
}

/**
 * Fetch the glossary from the static asset path.  Tolerant: a missing or
 * malformed file produces an empty glossary instead of a runtime crash —
 * the explorer remains usable, just without tooltips.
 */
export async function loadGlossary(url = `${import.meta.env.BASE_URL}data/glossary.json`): Promise<Glossary> {
  try {
    const res = await fetch(url);
    if (!res.ok) return emptyGlossary();
    const json = (await res.json()) as Glossary;
    return json;
  } catch {
    return emptyGlossary();
  }
}

function emptyGlossary(): Glossary {
  return { defender: {}, stargate: {}, robotron: {}, terms: {} };
}

/** Look up an entry by game + hex code (0..0x3F).  Returns undefined for unknown. */
export function lookup(
  glossary: Glossary,
  game: GameKind,
  cmd: number,
): GlossaryEntry | undefined {
  const key = (cmd & 0xFF).toString(16).toUpperCase().padStart(2, "0");
  return glossary[game]?.[key];
}

/**
 * Look up a technique/engine term explanation.  Keys are mixed-case (e.g.
 * "duty cycle", "mid-rail", "BRA-self"), so match exactly first, then fall
 * back to the legacy all-upper-case form, then a case-insensitive scan.
 */
export function lookupTerm(glossary: Glossary, term: string): TermEntry | undefined {
  const terms = glossary.terms;
  if (!terms) return undefined;
  if (term in terms) return terms[term];
  const upper = term.toUpperCase();
  if (upper in terms) return terms[upper];
  const lower = term.toLowerCase();
  for (const key of Object.keys(terms)) {
    if (key.toLowerCase() === lower) return terms[key];
  }
  return undefined;
}

/** One-line summary for tooltips: "LITE — Lightning (LFSR) — upward LFSR sweep". */
export function summarize(entry: GlossaryEntry): string {
  const parts: string[] = [];
  if (entry.routine) parts.push(entry.routine);
  if (entry.name) parts.push(`— ${entry.name}`);
  if (entry.engine) parts.push(`(${entry.engine})`);
  if (entry.blurb) parts.push(`— ${entry.blurb}`);
  return parts.join(" ");
}
